import { OrderItem as OrderItemType } from "@/redux/services/order/order.type";
import { cn, formatPrice } from "@/lib/utils";
import { useAppSelector } from "@/redux/features/hooks";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const OrderItemDetail: React.FC<{ item: OrderItemType; currency: string }> = ({
  item,
  currency,
}) => {
  const currentRestaurant = useAppSelector(
    (state) => state.user.currentRestaurant
  );

  const calculateUniqueItemPrice = () => {
    if (item.dailyDish) {
      return item.dailyDish.price / 100;
    }

    const basePrice = (item.menuItem?.basePrice ?? 0) / 100;
    let totalPriceModifier = 0;

    item.selectedMenuItemOptionValues?.forEach((selectedValue) => {
      const itemOption = item.menuItem?.itemOptions?.find(
        (option) =>
          option.menuItemOption?.id === selectedValue.menuItemOption?.id
      );

      if (!itemOption) return;

      const optionPrice = itemOption.itemOptionPrices?.find(
        (price) => price.menuItemOptionValue?.id === selectedValue.id
      );

      if (optionPrice) {
        totalPriceModifier += (optionPrice.priceModifier ?? 0) / 100;
        return;
      }

      if (itemOption.menuItemOption?.isTemplate) {
        totalPriceModifier += (selectedValue.basePrice ?? 0) / 100;
        return;
      }

      if (itemOption.menuItemOption?.dependsOnOption) {
        const parentOptionId = itemOption.menuItemOption.dependsOnOption.id;
        const parentSelectedValue = item.selectedMenuItemOptionValues?.find(
          (value) => value.menuItemOption?.id === parentOptionId
        );

        if (parentSelectedValue) {
          const dependentPrice = selectedValue.valueDependentPrices?.find(
            (price) => price.dependsOnValue?.id === parentSelectedValue.id
          );

          if (dependentPrice) {
            totalPriceModifier += (dependentPrice.priceModifier ?? 0) / 100;
          }
        }
      }
    });

    return basePrice + totalPriceModifier;
  };

  const groupedOptions =
    item.selectedMenuItemOptionValues?.reduce(
      (acc, option) => {
        const key = option.menuItemOption?.title ?? "Unknown";
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(option);
        return acc;
      },
      {} as Record<string, typeof item.selectedMenuItemOptionValues>
    ) ?? {};

  const sortedGroupedOptions = Object.entries(groupedOptions).sort(
    ([a], [b]) => {
      const sortOrderA = groupedOptions[a]?.[0]?.menuItemOption?.sortOrder ?? 0;
      const sortOrderB = groupedOptions[b]?.[0]?.menuItemOption?.sortOrder ?? 0;
      return sortOrderA - sortOrderB;
    }
  );

  return (
    <div
      className={cn(
        "flex items-start gap-3 py-2 px-2 rounded-lg",
        item.isModified ? "bg-yellow-50" : ""
      )}
    >
      <img
        src={
          (typeof item?.dailyDish?.image === "object"
            ? item?.dailyDish?.image?.url
            : null) ??
          item?.menuItem?.image?.url ??
          currentRestaurant?.image?.url ??
          "/images/icon.png"
        }
        alt={item.dailyDish?.name ?? item.menuItem?.title ?? "Item"}
        className="rounded-lg h-10 w-10 object-cover flex-shrink-0"
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-medium text-card-foreground">
              {item.dailyDish ? (
                <span className="flex items-center gap-1.5">
                  {item.dailyDish.name}
                  <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-[10px] font-medium">
                    Plat du jour
                  </span>
                </span>
              ) : (
                item.menuItem?.title ?? ""
              )}
            </p>
            <p className="text-xs mt-0.5">
              {formatPrice(calculateUniqueItemPrice(), currency)}
            </p>
            {sortedGroupedOptions.map(([optionTitle, values]) => (
              <p key={optionTitle} className="text-xs text-muted-foreground">
                {optionTitle}:{" "}
                {values.map((v) => v.title).join(", ")}
              </p>
            ))}
            {item.additionalDemands && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Note: {item.additionalDemands}
              </p>
            )}
          </div>

          <div className="flex flex-col items-end flex-shrink-0">
            {item.isModified && (
              <span className="bg-yellow-200 text-yellow-800 px-1.5 py-0.5 rounded text-[10px] font-medium mb-1">
                Modifié
                {item.lastModifiedAt &&
                  ` ${format(new Date(item.lastModifiedAt), "HH'h'mm", { locale: fr })}`}
              </span>
            )}
            <span className="text-sm text-muted-foreground">
              x{item.quantity}
            </span>
            <span className="text-sm font-semibold text-card-foreground">
              {formatPrice(item.price / 100, currency)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderItemDetail;
